// AreaPricing.js — Set restaurant item prices per area (floor).
// Pick an area, enter the price for each item (blank/0 → item's base price is
// used). The POS applies the area price for the selected table's area.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, MenuItem, Select, FormControl, InputLabel,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, Button, CircularProgress, Snackbar, Alert, Paper, InputAdornment,
  FormControlLabel, Checkbox,
} from '@mui/material';
import config from '../../config';
import * as XLSX from 'xlsx';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const token = sessionStorage.getItem('token');
const currency = company?.currency ?? '₹';

const areaName = (a) =>
  a?.areaName ?? a?.AreaName ?? a?.name ?? a?.area ?? `Area ${a?.id}`;

export default function AreaPricing() {
  const [areas, setAreas] = useState([]);
  const [areaId, setAreaId] = useState('');
  const [items, setItems] = useState([]);
  const [allPrices, setAllPrices] = useState([]); // every area/room price row
  const [prices, setPrices] = useState({}); // itemId -> price string (current scope)
  const [mode, setMode] = useState('area'); // 'area' | 'room' | 'table'
  const [rooms, setRooms] = useState([]);
  const [roomNumber, setRoomNumber] = useState('');
  const [allRooms, setAllRooms] = useState(false); // apply prices to every room
  const [tables, setTables] = useState([]); // tables of the selected area
  const [tableNo, setTableNo] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  useEffect(() => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      fetch(`${config.API_BASE_URL}/Home/GetAreas/${company?.id}`, auth)
        .then((r) => r.json())
        .catch(() => []),
      fetch(`${config.API_BASE_URL}/Home/GetallItemsAsync/${company?.id}`, auth)
        .then((r) => r.json())
        .catch(() => []),
      fetch(`${config.API_BASE_URL}/areapricing/${company?.id}`, auth)
        .then((r) => r.json())
        .catch(() => []),
      fetch(`${config.API_BASE_URL}/Home/GetAllRoom/${company?.id}`, auth)
        .then((r) => r.json())
        .catch(() => []),
    ])
      .then(([a, it, p, rm]) => {
        const areaList = Array.isArray(a) ? a : [];
        setAreas(areaList);
        if (areaList.length) setAreaId(areaList[0].id);
        setItems(
          (Array.isArray(it) ? it : [])
            .slice()
            .sort((x, y) =>
              (x.name || '').localeCompare(y.name || '', undefined, {
                sensitivity: 'base',
              }),
            ),
        );
        setAllPrices(Array.isArray(p) ? p : []);
        const roomList = Array.isArray(rm) ? rm : [];
        setRooms(roomList);
        if (roomList.length) setRoomNumber(roomList[0].roomnumber);
      })
      .finally(() => setLoading(false));
  }, []);

  // Load the tables of the selected area when pricing by table.
  useEffect(() => {
    if (mode !== 'table' || !areaId) {
      setTables([]);
      return;
    }
    fetch(`${config.API_BASE_URL}/Home/GetTables/${areaId}/${company?.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setTables(list);
        if (list.length) setTableNo(String(list[0].tableName));
        else setTableNo('');
      })
      .catch(() => setTables([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, areaId]);

  // Prefill the inputs for the selected scope (area / room / table).
  useEffect(() => {
    const map = {};
    if (mode === 'table') {
      if (!tableNo) {
        setPrices({});
        return;
      }
      allPrices
        .filter((p) => (p.tableNo || '') === tableNo)
        .forEach((p) => {
          map[p.itemId] = String(p.price);
        });
      setPrices(map);
      return;
    }
    if (mode === 'room') {
      if (!roomNumber) {
        setPrices({});
        return;
      }
      allPrices
        .filter((p) => Number(p.roomNumber) === Number(roomNumber))
        .forEach((p) => {
          map[p.itemId] = String(p.price);
        });
    } else {
      if (!areaId) {
        setPrices({});
        return;
      }
      allPrices
        .filter(
          (p) => Number(p.areaId) === Number(areaId) && p.roomNumber == null,
        )
        .forEach((p) => {
          map[p.itemId] = String(p.price);
        });
    }
    setPrices(map);
  }, [mode, areaId, roomNumber, tableNo, allPrices]);

  const scopeMissing =
    mode === 'room'
      ? allRooms
        ? rooms.length === 0
        : !roomNumber
      : mode === 'table'
        ? !tableNo
        : !areaId;

  const handleSave = async () => {
    if (scopeMissing) return;
    setSaving(true);
    try {
      const pricesPayload = items.map((i) => ({
        itemId: i.id,
        price: Number(prices[i.id]) || 0,
      }));
      const postFor = (body) =>
        fetch(`${config.API_BASE_URL}/areapricing/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });

      if (mode === 'room' && allRooms) {
        // Apply the same prices to EVERY room.
        for (const r of rooms) {
          const res = await postFor({
            companyId: company?.id,
            areaId: 0,
            roomNumber: Number(r.roomnumber),
            tableNo: null,
            prices: pricesPayload,
          });
          if (!res.ok) throw new Error('Failed');
        }
      } else {
        const res = await postFor({
          companyId: company?.id,
          areaId: mode === 'area' ? Number(areaId) : 0,
          roomNumber: mode === 'room' ? Number(roomNumber) : null,
          tableNo: mode === 'table' ? tableNo : null,
          prices: pricesPayload,
        });
        if (!res.ok) throw new Error('Failed');
      }

      const fresh = await fetch(
        `${config.API_BASE_URL}/areapricing/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      ).then((r) => r.json());
      setAllPrices(Array.isArray(fresh) ? fresh : []);
      setSnack({
        open: true,
        msg:
          mode === 'room' && allRooms
            ? `Prices saved to all ${rooms.length} rooms.`
            : 'Prices saved.',
        sev: 'success',
      });
    } catch {
      setSnack({ open: true, msg: 'Failed to save.', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Excel: download a template and import prices for the current scope ──
  const downloadTemplate = () => {
    const sample =
      items.length > 0
        ? items.map((i) => ({ ItemName: i.name, Price: '' }))
        : [{ ItemName: 'Sample Item', Price: 100 }];
    const ws = XLSX.utils.json_to_sheet(sample);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Prices');
    XLSX.writeFile(wb, 'AreaPrice_Template.xlsx');
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], {
          defval: '',
        });
        const byName = {};
        items.forEach((i) => {
          byName[(i.name || '').trim().toLowerCase()] = i.id;
        });
        const next = { ...prices };
        let matched = 0;
        let missed = 0;
        rows.forEach((r) => {
          const name = String(r.ItemName ?? r.itemName ?? r.Name ?? '')
            .trim()
            .toLowerCase();
          const price = Number(r.Price ?? r.price ?? 0);
          if (!name) return;
          const id = byName[name];
          if (id != null && price > 0) {
            next[id] = String(price);
            matched++;
          } else {
            missed++;
          }
        });
        setPrices(next);
        setSnack({
          open: true,
          msg: `Imported ${matched} price(s)${
            missed ? `, ${missed} not matched` : ''
          }. Ab "Save Prices" dabao.`,
          sev: matched ? 'success' : 'warning',
        });
      } catch {
        setSnack({ open: true, msg: 'Excel padhne me dikkat.', sev: 'error' });
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const visible = search.trim()
    ? items.filter((i) =>
        (i.name || '').toLowerCase().includes(search.toLowerCase().trim()),
      )
    : items;

  return (
    <Container maxWidth='md' sx={{ py: 3 }}>
      <Typography variant='h5' fontWeight={700} mb={2}>
        Area / Room / Table-wise Item Pricing
      </Typography>

      <Box display='flex' gap={2} mb={2} flexWrap='wrap' alignItems='center'>
        <FormControl size='small' sx={{ minWidth: 150 }}>
          <InputLabel>Price for</InputLabel>
          <Select
            label='Price for'
            value={mode}
            onChange={(e) => setMode(e.target.value)}>
            <MenuItem value='area'>By Area</MenuItem>
            <MenuItem value='room'>By Room</MenuItem>
            <MenuItem value='table'>By Table</MenuItem>
          </Select>
        </FormControl>
        {(mode === 'area' || mode === 'table') && (
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel>Area</InputLabel>
            <Select
              label='Area'
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}>
              {areas.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {areaName(a)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {mode === 'table' && (
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel>Table</InputLabel>
            <Select
              label='Table'
              value={tableNo}
              onChange={(e) => setTableNo(e.target.value)}>
              {tables.map((t) => (
                <MenuItem key={t.tableId ?? t.tableName} value={String(t.tableName)}>
                  {t.tableName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {mode === 'room' && (
          <>
            <FormControl size='small' sx={{ minWidth: 200 }}>
              <InputLabel>Room</InputLabel>
              <Select
                label='Room'
                value={roomNumber}
                disabled={allRooms}
                onChange={(e) => setRoomNumber(e.target.value)}>
                {rooms.map((r) => (
                  <MenuItem key={r.roomnumber} value={r.roomnumber}>
                    {r.roomName ? r.roomName : `Room ${r.roomnumber}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allRooms}
                  onChange={(e) => setAllRooms(e.target.checked)}
                />
              }
              label='All rooms'
            />
          </>
        )}
        <TextField
          size='small'
          placeholder='Search items...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <Box flex={1} />
        <Button variant='text' onClick={downloadTemplate}>
          Template
        </Button>
        <Button variant='outlined' component='label'>
          Import Excel
          <input
            type='file'
            hidden
            accept='.xlsx,.xls,.csv'
            onChange={handleImport}
          />
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={saving || scopeMissing}>
          {saving ? 'Saving...' : 'Save Prices'}
        </Button>
      </Box>

      <Typography variant='body2' color='text.secondary' mb={1}>
        Blank ya 0 chhodo to us item ka base price use hoga.
      </Typography>

      {loading ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Item</TableCell>
                <TableCell align='right'>Base Price</TableCell>
                <TableCell align='right' sx={{ width: 160 }}>
                  Area Price
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align='center'>
                    No items found.
                  </TableCell>
                </TableRow>
              )}
              {visible.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>{i.name}</TableCell>
                  <TableCell align='right'>
                    {currency} {Number(i.price || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align='right'>
                    <TextField
                      size='small'
                      type='number'
                      placeholder={Number(i.price || 0).toFixed(0)}
                      value={prices[i.id] ?? ''}
                      onChange={(e) =>
                        setPrices((prev) => ({
                          ...prev,
                          [i.id]: e.target.value,
                        }))
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            {currency}
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: 140 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
